import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Button, Flag, Heading, Message, Tooltip } from '@lumx/react';
import {
  mdiArrowLeft, mdiArrowRight, mdiAutoFix, mdiBullhornOutline, mdiCalendar,
  mdiCalendarCheck, mdiCalendarEdit, mdiCheckCircle, mdiChevronLeft, mdiChevronRight,
  mdiClose, mdiDownloadOutline, mdiEarth, mdiEmailOutline, mdiFileImageOutline,
  mdiFilePdfBox, mdiMapMarkerOutline, mdiMenuDown, mdiMicrosoftExcel,
  mdiLan, mdiOpenInNew, mdiPencilOutline, mdiPostOutline, mdiServer,
} from '@mdi/js';
import MdiIcon from '@mdi/react';
import { useWizard } from '../context/WizardContext';

// ── Date helpers ─────────────────────────────────────────────────────────────

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['Mo','Tu','We','Th','Fr','Sa','Su'];

function startOfDay(d: Date): Date { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function sameDay(a: Date | null, b: Date | null): boolean {
  return !!(a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate());
}
function fmtDate(d: Date | null): string {
  if (!d) return '';
  const dow = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
  return `${dow}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// ── DatePickerLumx ────────────────────────────────────────────────────────────

const DatePickerLumx: React.FC<{
  value: Date | null;
  onChange: (d: Date | null) => void;
  minDate: Date;
  helper?: string;
}> = ({ value, onChange, minDate, helper }) => {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState<Date>(() => startOfDay(value || minDate));
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const offset = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = addDays(firstOfMonth, -offset);
  const today = startOfDay(new Date());
  const min = startOfDay(minDate);
  const days: Date[] = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  const prevDisabled = startOfDay(new Date(year, month, 0)) < min;

  const pick = (d: Date) => { onChange(startOfDay(d)); setOpen(false); };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <label className="dep-field__label">Deployment date</label>
      <div
        className="dep-field__wrap"
        onClick={e => { if ((e.target as Element).closest('.dep-clear')) return; setOpen(o => !o); }}
      >
        <MdiIcon path={mdiCalendar} size={0.8} color="rgba(0,0,0,0.45)" />
        <input
          readOnly
          className="dep-input"
          placeholder="Select a date"
          value={fmtDate(value)}
          style={{ cursor: 'pointer', paddingRight: value ? 36 : 12 }}
        />
        {value && (
          <button type="button" className="dep-clear" aria-label="Clear date"
            onClick={e => { e.stopPropagation(); onChange(null); setOpen(false); }}>
            <MdiIcon path={mdiClose} size={0.7} />
          </button>
        )}
      </div>
      {helper && <span className="dep-field__helper">{helper}</span>}

      {open && (
        <div className="dep-cal" role="dialog">
          <div className="dep-cal__head">
            <button type="button" disabled={prevDisabled}
              onClick={() => setCursor(new Date(year, month - 1, 1))}>
              <MdiIcon path={mdiChevronLeft} size={0.9} />
            </button>
            <div className="dep-cal__title">{MONTH_NAMES[month]} {year}</div>
            <button type="button" onClick={() => setCursor(new Date(year, month + 1, 1))}>
              <MdiIcon path={mdiChevronRight} size={0.9} />
            </button>
          </div>
          <div className="dep-cal__grid">
            {DOW.map(d => <span key={d} className="dep-cal__dow">{d}</span>)}
            {days.map((d, i) => {
              const inMonth = d.getMonth() === month;
              const disabled = d < min;
              const selected = sameDay(d, value);
              const isToday = sameDay(d, today);
              return (
                <button key={i} type="button" disabled={disabled}
                  className={['dep-cal__day', !inMonth && 'dep-cal__day--other', isToday && 'dep-cal__day--today', selected && 'dep-cal__day--sel'].filter(Boolean).join(' ')}
                  onClick={() => pick(d)}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="dep-cal__foot">
            <button type="button" onClick={() => pick(addDays(today, 14))}>In 2 weeks</button>
            <button type="button" onClick={() => pick(addDays(today, 21))}>In 3 weeks</button>
            <button type="button" onClick={() => pick(addDays(today, 30))}>In 1 month</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── DownloadMenu ──────────────────────────────────────────────────────────────

const DownloadMenu: React.FC<{ onSelect: (k: string) => void }> = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const pick = (k: string) => { setOpen(false); onSelect(k); };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Button emphasis="medium" color="dark" leftIcon={mdiDownloadOutline} rightIcon={mdiMenuDown}
        onClick={() => setOpen(o => !o)}>Download</Button>
      {open && (
        <div className="dep-dl-menu" role="menu">
          <div className="dep-dl-menu__section">Visual summary</div>
          <button className="dep-dl-menu__item" onClick={() => pick('png')}>
            <MdiIcon path={mdiFileImageOutline} size={0.85} color="var(--lumx-color-primary-N)" />
            <div><div>Download as PNG</div><small>Diagram only · ~600 KB</small></div>
          </button>
          <button className="dep-dl-menu__item" onClick={() => pick('pdf')}>
            <MdiIcon path={mdiFilePdfBox} size={0.85} color="var(--lumx-color-red-N)" />
            <div><div>Download as PDF</div><small>Diagram + legend · A4 landscape</small></div>
          </button>
          <div className="dep-dl-menu__divider" />
          <div className="dep-dl-menu__section">Full structure</div>
          <button className="dep-dl-menu__item" onClick={() => pick('xlsx')}>
            <MdiIcon path={mdiMicrosoftExcel} size={0.85} color="var(--lumx-color-green-N)" />
            <div><div>Download as Excel (.xlsx)</div><small>Org units, locations, sites, groups</small></div>
          </button>
        </div>
      )}
    </div>
  );
};

// ── Visual summary ────────────────────────────────────────────────────────────

type VSKind = 'global' | 'org' | 'loc' | 'site';
const VS_ICON: Record<VSKind, string> = { global: mdiEarth, org: mdiLan, loc: mdiMapMarkerOutline, site: mdiServer };
const VS_COLOR: Record<VSKind, string> = { global: '#245be7', org: '#7c3aed', loc: '#b87c00', site: '#138669' };

const VSCard: React.FC<{ kind: VSKind; title: string; style?: React.CSSProperties }> = ({ kind, title, style }) => (
  <div className={`vs-node vs-node--${kind}`} style={style}>
    <div className="vs-node__icon" style={{ background: VS_COLOR[kind] + '18', color: VS_COLOR[kind] }}>
      <MdiIcon path={VS_ICON[kind]} size={0.75} />
    </div>
    <div className="vs-node__label">{title}</div>
  </div>
);

const VisualSummary: React.FC = () => (
  <div className="vs-wrap">
    <div className="vs-frame">
      <div className="vs-frame__pill">BEFORE: FLAT HIERARCHY</div>
      <div className="vs-before">
        <VSCard kind="global" title="Global Platform" />
        <svg className="vs-svg" viewBox="0 0 320 60" preserveAspectRatio="none" aria-hidden>
          <path d="M160 0 V20 H40 V60 M160 20 H88 V60 M160 20 V60 M160 20 H232 V60 M160 20 H280 V60" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" fill="none" />
        </svg>
        <div className="vs-before__sites">
          {[0,1,2,3,4].map(i => <VSCard key={i} kind="site" title="Site" />)}
          <div className="vs-ellipsis">…</div>
        </div>
        <div className="vs-note">All sites attached directly to Global Platform.</div>
      </div>
    </div>

    <div className="vs-arrow" aria-hidden><MdiIcon path={mdiArrowRight} size={1.1} /></div>

    <div className="vs-frame vs-frame--wide">
      <div className="vs-frame__pill vs-frame__pill--after">AFTER: MULTI-LEVEL HIERARCHY</div>
      <div className="vs-after">
        <div className="vs-after__top"><VSCard kind="global" title="Global Platform" /></div>
        <svg className="vs-svg vs-svg--after-1" viewBox="0 0 560 40" preserveAspectRatio="none" aria-hidden>
          <path d="M280 0 V14 H56 V40 M280 14 H168 V40 M280 14 V40 M280 14 H392 V40 M280 14 H504 V40" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" fill="none" />
        </svg>
        <div className="vs-after__row vs-after__orgs">
          {['Org Unit 1','Org Unit 2','Org Unit 3','Org Unit 4','Org Unit 5'].map(t => <VSCard key={t} kind="org" title={t} />)}
          <div className="vs-ellipsis vs-ellipsis--inline">…</div>
        </div>
        <div className="vs-after__row vs-after__sites-on-org">
          {[0,1,2,3,4].map(i => (
            <div key={i} className="vs-org-site">
              <svg className="vs-attach" viewBox="0 0 2 22" preserveAspectRatio="none" aria-hidden>
                <path d="M1 0 V22" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" strokeDasharray="3 3" fill="none" />
              </svg>
              <VSCard kind="site" title="Site" />
            </div>
          ))}
        </div>
        <svg className="vs-svg vs-svg--after-2" viewBox="0 0 560 28" preserveAspectRatio="none" aria-hidden>
          {[56,168,280,392,504].map((cx, i) => (
            <g key={i}>
              <path d={`M${cx} 0 V14 H${cx-22} V28 M${cx} 14 V28 M${cx} 14 H${cx+22} V28`} stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" fill="none" />
            </g>
          ))}
        </svg>
        <div className="vs-after__row vs-after__locs">
          {[['1.1','1.2'],['2.1','2.2'],['3.1','3.2','3.3'],['4.1','4.2'],['5.1','5.2']].map((arr, i) => (
            <div key={i} className="vs-loc-cluster">
              {arr.map(s => <VSCard key={s} kind="loc" title={`Loc ${s}`} />)}
              <div className="vs-ellipsis vs-ellipsis--mini">…</div>
            </div>
          ))}
        </div>
        <div className="vs-after__row vs-after__sites-on-loc">
          {[0,1,2,3,4].map(i => (
            <div key={i} className="vs-loc-site">
              <svg className="vs-attach" viewBox="0 0 2 22" preserveAspectRatio="none" aria-hidden>
                <path d="M1 0 V22" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" strokeDasharray="3 3" fill="none" />
              </svg>
              <VSCard kind="site" title="Site" />
            </div>
          ))}
        </div>
        <div className="vs-note">Sites attach to an Org Unit or a specific Location.</div>
      </div>
    </div>

    <div className="vs-legend">
      <div className="vs-legend__label">LEGEND</div>
      <div className="vs-legend__items">
        {(['org','loc','site'] as VSKind[]).map(k => (
          <div key={k} className="vs-legend__item">
            <div className="vs-node__icon vs-node__icon--sm" style={{ background: VS_COLOR[k] + '18', color: VS_COLOR[k] }}>
              <MdiIcon path={VS_ICON[k]} size={0.65} />
            </div>
            <div>
              <div className="vs-legend__title">{k === 'org' ? 'Org Unit' : k === 'loc' ? 'Location' : 'Site'}</div>
              <div className="vs-legend__sub">{k === 'org' ? 'Intermediate level' : k === 'loc' ? 'Leaf node' : 'Attached site'}</div>
            </div>
          </div>
        ))}
        <div className="vs-legend__item">
          <div className="vs-legend__lines">
            <span className="vs-legend__line vs-legend__line--solid" />
            <span className="vs-legend__line vs-legend__line--dashed" />
          </div>
          <div>
            <div className="vs-legend__title">Hierarchy / attachment</div>
            <div className="vs-legend__sub">Dashed = site attachment</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── SubPage (communication editor placeholder) ────────────────────────────────

type CommKind = 'broadcast' | 'email' | 'blog';

const COMM_META: Record<CommKind, { title: string; icon: string; lede: string }> = {
  broadcast: { title: 'Generate broadcast', icon: mdiBullhornOutline, lede: 'Send a platform-wide broadcast to announce the new governance structure.' },
  email:     { title: 'AI-generated email',     icon: mdiEmailOutline,    lede: 'Draft a personalized email to send through your existing channels.' },
  blog:      { title: 'AI-generated blog post', icon: mdiPostOutline,     lede: 'Publish a longer article explaining the change and what users should expect.' },
};

const SubPage: React.FC<{ kind: CommKind; onBack: () => void; deployDate: Date | null }> = ({ kind, onBack, deployDate }) => {
  const meta = COMM_META[kind];
  return (
    <div style={{ padding: '24px 24px', maxWidth: 780 }}>
      <button className="dep-crumb" onClick={onBack}>
        <MdiIcon path={mdiArrowLeft} size={0.8} />Back to Deployment
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, marginTop: 16 }}>
        <div className="dep-comm-icon" style={{ width: 44, height: 44 }}>
          <MdiIcon path={meta.icon} size={1.1} />
        </div>
        <Heading as="h1" typography="title">{meta.title}</Heading>
      </div>
      <p style={{ color: 'rgba(0,0,0,0.60)', marginBottom: 24 }}>{meta.lede}</p>
      <Message kind="info" hasBackground style={{ marginBottom: 24 }}>
        This is a destination page placeholder — it demonstrates that each communication option navigates to its own editor. The actual content lives in a separate flow.
      </Message>
      <div className="dep-preview-card">
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(0,0,0,0.45)', marginBottom: 8 }}>Preview</div>
        <h3 style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}>Your new platform is changing</h3>
        <p style={{ fontSize: 14, marginBottom: 8 }}>Hi <em>{'{first_name}'}</em>,</p>
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          On <strong>{deployDate ? fmtDate(deployDate) : '{deployment_date}'}</strong> we are deploying a new governance structure for the LumApps platform. Your site will move from a flat structure into a multi-level hierarchy organized by Org Unit and Location.
        </p>
        <p style={{ fontSize: 14, marginBottom: 4 }}>What this means for you:</p>
        <ul style={{ paddingLeft: 20, fontSize: 14, marginBottom: 16 }}>
          <li>Your bookmarks and feeds will be migrated automatically.</li>
          <li>You may see new content surfaced from your Org Unit.</li>
          <li>Mobile app users will be prompted to re-confirm their location once.</li>
        </ul>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 12 }}>
          <Button emphasis="low" color="primary" leftIcon={mdiAutoFix}>Regenerate</Button>
          <Button emphasis="low" color="dark" leftIcon={mdiPencilOutline}>Edit</Button>
          <div style={{ flex: 1 }} />
          <Button emphasis="high" color="primary" leftIcon={mdiCheckCircle}>Save &amp; mark as ready</Button>
        </div>
      </div>
    </div>
  );
};

// ── DeploymentPage ────────────────────────────────────────────────────────────

const DeploymentPage: React.FC = () => {
  const { navigateTo, scheduleDeployment, deploymentScheduled, deploymentDate: savedDate, groupsConfirmed } = useWizard();
  const [deployDate, setDeployDate] = useState<Date | null>(savedDate);
  const [scheduled, setScheduled] = useState(deploymentScheduled);
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [subpage, setSubpage] = useState<CommKind | null>(null);

  const minDate = useMemo(() => addDays(startOfDay(new Date()), 14), []);
  const daysAway = deployDate ? Math.round((deployDate.getTime() - startOfDay(new Date()).getTime()) / 86400000) : null;
  const helper = deployDate ? `${daysAway} days from today.` : 'Choose a date at least 14 days from today.';

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }, []);

  const handleSchedule = () => {
    setConfirming(false);
    setScheduled(true);
    if (deployDate) scheduleDeployment(deployDate);
  };

  if (subpage) {
    return (
      <div className="main-content" style={{ overflow: 'auto' }}>
        <SubPage kind={subpage} onBack={() => setSubpage(null)} deployDate={deployDate} />
      </div>
    );
  }

  if (scheduled && deployDate) {
    return (
      <div className="main-content" style={{ overflow: 'auto' }}>
        <div className="page-header">
          <div className="page-header-left">
            <Heading as="h1" typography="headline">Deployment</Heading>
            <Flag color="green" label="SCHEDULED" />
          </div>
        </div>
        <div className="dep-success">
          <div className="dep-success__icon">
            <MdiIcon path={mdiCalendarCheck} size={2.5} color="var(--lumx-color-green-N)" />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '16px 0 8px' }}>Deployment scheduled</h2>
          <p style={{ color: 'rgba(0,0,0,0.60)', marginBottom: 20 }}>Your new governance structure will deploy on the date below. Users will receive your selected communications in the lead-up.</p>
          <div className="dep-success__date">
            <MdiIcon path={mdiCalendar} size={0.85} />
            {fmtDate(deployDate)}
          </div>
          <div className="dep-success__actions">
            <Button emphasis="medium" color="dark" leftIcon={mdiCalendarEdit} onClick={() => setScheduled(false)}>Reschedule</Button>
            <Button emphasis="high" color="primary" rightIcon={mdiArrowRight} onClick={() => navigateTo('migration-progress')}>Back to Migration progress</Button>
          </div>
        </div>
      </div>
    );
  }

  const commOptions: { key: CommKind; icon: string; title: string; desc: string }[] = [
    { key: 'broadcast', icon: mdiBullhornOutline, title: 'Generate broadcast',       desc: 'In-product announcement pinned to every site for 14 days.' },
    { key: 'email',     icon: mdiEmailOutline,    title: 'AI-generated email',        desc: 'Personalized email tuned to your audience and tone.' },
    { key: 'blog',      icon: mdiPostOutline,     title: 'AI-generated blog post',    desc: 'Long-form article explaining the change and FAQ.' },
  ];

  return (
    <div className="main-content" style={{ overflow: 'auto' }}>
      <div className="page-header">
        <div className="page-header-left">
          <Heading as="h1" typography="headline">Deployment</Heading>
        </div>
        <div className="page-header-right">
          <Tooltip
            label={!deployDate ? 'Select deployment date to schedule' : null}
            placement="bottom"
          >
            <span>
              <Button emphasis="high" disabled={!deployDate} onClick={() => setConfirming(true)}>
                Schedule deployment
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        <Message kind="info" hasBackground>
          You are ready to deploy your new governance structure. Choose a date (<strong>at least 14 days ahead</strong>) and inform your users about the change with the templates provided below.
        </Message>
      </div>

      <div className="dep-grid">
        <div>
          <h2 className="dep-section-title">Date &amp; Communication</h2>
          <div style={{ marginBottom: 24 }}>
            <DatePickerLumx value={deployDate} onChange={setDeployDate} minDate={minDate} helper={helper} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'rgba(0,0,0,0.87)', marginBottom: 6 }}>Inform your users</div>
          <p style={{ color: 'rgba(0,0,0,0.60)', fontSize: 13, marginBottom: 12 }}>Choose one or more channels. Each opens its own editor.</p>
          <div className="dep-comm-list">
            {commOptions.map(opt => (
              <button key={opt.key} className="dep-comm-item" onClick={() => setSubpage(opt.key)}>
                <div className="dep-comm-icon">
                  <MdiIcon path={opt.icon} size={0.9} />
                </div>
                <div className="dep-comm-body">
                  <div className="dep-comm-title">
                    {opt.title}
                    <MdiIcon path={mdiOpenInNew} size={0.6} color="rgba(0,0,0,0.38)" />
                  </div>
                  <div className="dep-comm-desc">{opt.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="dep-summary-panel">
          <div className="dep-summary-header">
            <h2 className="dep-section-title" style={{ margin: 0 }}>Visual summary</h2>
            <DownloadMenu onSelect={msg => showToast(msg === 'png' ? 'Downloading visual summary (PNG)…' : msg === 'pdf' ? 'Downloading visual summary (PDF)…' : 'Downloading full structure (Excel)…')} />
          </div>
          <VisualSummary />
        </div>
      </div>

      {confirming && (
        <div className="dep-modal-overlay" onClick={() => setConfirming(false)}>
          <div className="dep-modal" onClick={e => e.stopPropagation()} role="dialog">
            <div className="dep-modal__head">Schedule deployment?</div>
            <div className="dep-modal__body">
              <p>This will lock the governance structure and begin user notifications on the date below.</p>
              <div className="dep-modal__row">
                <div className="dep-modal__lbl">Deployment date</div>
                <div className="dep-modal__val">{fmtDate(deployDate)}</div>
              </div>
              <div className="dep-modal__row">
                <div className="dep-modal__lbl">Notifications</div>
                <div className="dep-modal__val">
                  Drafted in separate tools
                  <br /><span style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>Broadcast, email and blog post managed in their own editors.</span>
                </div>
              </div>
              <div className="dep-modal__row">
                <div className="dep-modal__lbl">Affected</div>
                <div className="dep-modal__val">5 Org Units · 12 Locations · 23 Sites</div>
              </div>
            </div>
            <div className="dep-modal__foot">
              <Button emphasis="low" color="dark" onClick={() => setConfirming(false)}>Cancel</Button>
              <Button emphasis="high" color="primary" leftIcon={mdiCheckCircle} onClick={handleSchedule}>
                Confirm &amp; schedule
              </Button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="dep-toast">
          <MdiIcon path={mdiDownloadOutline} size={0.8} />
          {toast}
        </div>
      )}
    </div>
  );
};

export default DeploymentPage;
